import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Row, Col, Card, Button, Dropdown, Spinner } from "react-bootstrap";
import { FiChevronLeft, FiMapPin, FiMail, FiPhone, FiMoreVertical, FiCheckCircle, FiUser, FiXCircle } from "react-icons/fi";
import { FaTruck, FaRupeeSign } from "react-icons/fa";
import Sidebar from "../../common/Sidebar";
import { getVendorEditApi, getProductListApi, updateVendorStatusApi, deleteVendorApi } from "./vendor-api";
import LoaderModal from "../../common/loader";
import DeleteModal from "../../common/deleteModel";
import moreIcon from "../../../assets/svg/more_ic.svg";
import { useGlobal } from "../../../GlobalContext";
import "../../common/skeleton-effect.css";
import CommonSnackbar from "../../common/reusable-commponent";
import { sanitizeInput } from "../../common/security-utils";

const VendorView = () => {
  const { rolePermissionList, userType } = useGlobal();
  const navigate = useNavigate();
  const location = useLocation();
  const id = location.state?.id;
  const [vendor, setVendor] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isStatusUpdating, setIsStatusUpdating] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [snackbar, setSnackbar] = useState({ show: false, message: "", status: false });
  const triggerSnackbar = (message, status) => {
    setSnackbar({ show: true, message: sanitizeInput(message), status });
  };
  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, show: false }));
  };

  function checkPermission(permission) {
    return rolePermissionList.findIndex((element) => element["name"] === "Vendor" && element["menu_permission"] === true && element[permission] === true) > -1 ? true : false;
  }

  useEffect(() => {
    if (deleteId !== null) {
      setIsOpenDelete(true);
    }
  }, [deleteId]);
  const deleteVendor = async () => {
    try {
      const response = await deleteVendorApi(deleteId);
      if (response && response.data && response.data.success === true) {
        triggerSnackbar(response.data.message || "Vendor deleted successfully", true);
        setTimeout(() => {
          navigate(-1);
          setDeleteId(null);
          setIsOpenDelete(false);
        }, 1000);
      } else {
        triggerSnackbar(response.data?.message || "Failed to delete vendor", false);
      }
    } catch {
      triggerSnackbar("An error occurred while deleting vendor", false);
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    const fetchData = async () => {
      if (id) {
        try {
          const response = await getVendorEditApi(id);
          if (response.data && response.data.success) {
            setVendor(response.data.vendors || response.data.data || response.data);
          } else {
            triggerSnackbar(response.data?.message || "Failed to fetch vendor details", false);
          }

          const productResponse = await getProductListApi({ vendor_id: id });
          if (productResponse.data && productResponse.data.success) {
            setProducts(productResponse.data.products || productResponse.data.data || []);
          }
        } catch {
          triggerSnackbar("An error occurred while fetching data", false);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchData();
  }, [id]);

  const handleStatusUpdate = async (newStatus) => {
    if (!vendor) return;
    setIsStatusUpdating(true);
    try {
      const response = await updateVendorStatusApi(vendor.id, {
        status: newStatus,
      });
      if (response && response.data && response.data.success) {
        triggerSnackbar(response.data.message || "Status updated successfully", true);
        setVendor((prev) => ({ ...prev, status: newStatus }));
      } else {
        triggerSnackbar(response.data?.message || "Failed to update status", false);
      }
    } catch {
      triggerSnackbar("An error occurred while updating status", false);
    } finally {
      setIsStatusUpdating(false);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div className="p-3">
          {/* Header Skeleton */}
          <div className="bg-white rounded shadow-sm p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex gap-3">
                <div className="skeleton" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
                <div className="d-flex gap-3">
                  <div className="skeleton" style={{ width: "48px", height: "48px", borderRadius: "12px" }} />
                  <div className="d-flex flex-column gap-2">
                    <div className="skeleton skeleton-text" style={{ width: "180px", height: "18px" }} />
                    <div className="skeleton skeleton-text-sm" style={{ width: "120px", height: "12px" }} />
                  </div>
                </div>
              </div>
              <div className="d-flex gap-2">
                <div className="skeleton skeleton-button" style={{ width: "100px", height: "32px" }} />
                <div className="skeleton" style={{ width: "36px", height: "36px", borderRadius: "50%" }} />
              </div>
            </div>
            <div className="d-flex align-items-center gap-3 mt-3">
              <div className="skeleton skeleton-text" style={{ width: "150px", height: "16px" }} />
              <div className="skeleton skeleton-text" style={{ width: "200px", height: "16px" }} />
            </div>
          </div>

          {/* Cards Skeleton */}
          <Row className="g-3 mb-3">
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100">
                <Card.Body className="p-4">
                  <div className="skeleton skeleton-text-lg mb-4" style={{ width: "130px" }} />
                  <div className="mb-3">
                    <div className="d-flex justify-content-between mb-2">
                      <div className="skeleton skeleton-text-sm" style={{ width: "80px" }} />
                      <div className="skeleton skeleton-text" style={{ width: "100px" }} />
                    </div>
                  </div>
                  <div className="mb-4">
                    <div className="d-flex justify-content-between mb-2">
                      <div className="skeleton skeleton-text-sm" style={{ width: "70px" }} />
                      <div className="skeleton skeleton-text" style={{ width: "120px" }} />
                    </div>
                  </div>
                  <div className="border rounded-4 p-3" style={{ borderStyle: "dashed" }}>
                    <div className="skeleton skeleton-text-sm mb-3" style={{ width: "180px" }} />
                    <div className="d-flex gap-3">
                      <div className="skeleton" style={{ width: "48px", height: "48px", borderRadius: "50%" }} />
                      <div className="flex-grow-1">
                        <div className="skeleton skeleton-text mb-2" style={{ width: "140px" }} />
                        <div className="skeleton skeleton-text-sm" style={{ width: "200px" }} />
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <div className="d-flex flex-column gap-3 h-100">
                <Card className="border-0 shadow-sm flex-fill">
                  <Card.Body className="p-4">
                    <div className="skeleton skeleton-text-lg mb-3" style={{ width: "120px" }} />
                    <div className="d-flex gap-3">
                      <div className="skeleton" style={{ width: "32px", height: "32px", borderRadius: "50%" }} />
                      <div className="flex-grow-1">
                        <div className="skeleton skeleton-text mb-2" style={{ width: "100%" }} />
                        <div className="skeleton skeleton-text-sm" style={{ width: "80%" }} />
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm flex-fill">
                  <Card.Body className="p-4">
                    <div className="skeleton skeleton-text-lg mb-3" style={{ width: "100px" }} />
                    <div className="skeleton skeleton-text" style={{ width: "100%", height: "60px" }} />
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <p>Vendor not found.</p>
      </div>
    );
  }

  return (
    <>
      <LoaderModal show={isLoading} />
      <DeleteModal
        title={"vendor"}
        isOpen={isOpenDelete}
        onClose={() => {
          setIsOpenDelete(false);
          setDeleteId(null);
        }}
        onDelete={deleteVendor}
      />
      <div
        style={{
          backgroundColor: "#f8f9fa",
          minHeight: "100vh",
          fontFamily: "'Inter', sans-serif",
        }}
      >
        <div className="mt-2">
          <div className="bg-white rounded shadow-sm p-3 mb-3">
            <div className="d-flex justify-content-between align-items-start">
              <div className="d-flex gap-3">
                <Button variant="light" className="rounded-circle d-flex align-items-center justify-content-center border" style={{ width: "40px", height: "40px" }} onClick={() => navigate(-1)}>
                  <FiChevronLeft size={20} />
                </Button>

                <div className="d-flex gap-3">
                  <div
                    className=" d-flex align-items-center justify-content-center overflow-hidden"
                    style={{
                      width: "48px",
                      height: "48px",
                      borderRadius: "12px",
                      backgroundColor: vendor.upload ? "" : "#FF3B30",
                    }}
                  >
                    {vendor.upload ? (
                      <img
                        src={vendor.upload}
                        alt="Vendor"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <FaTruck color="white" size={24} />
                    )}
                  </div>

                  <div className="text-start">
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <h4 className="m-0 fw-bold fs-18">{sanitizeInput(String(vendor.vendor_name || ""))}</h4>
                    </div>
                    <div className="fs-12 text-muted">{sanitizeInput(String(vendor.type || ""))}</div>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-column align-items-end gap-2">
                <div className="d-flex align-items-center gap-2">
                  {checkPermission("edit_permission") && (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="white"
                        className="border rounded-pill px-3 py-1 d-flex align-items-center gap-2 fs-13"
                        style={{
                          color: vendor.status ? "#198754" : "#dc3545",
                        }}
                      >
                        {isStatusUpdating ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <>
                            <span className={`rounded-circle ${vendor.status ? "bg-success" : "bg-danger"}`} style={{ width: "8px", height: "8px" }}></span>
                            {vendor.status ? "Active" : "Inactive"}
                          </>
                        )}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item onClick={() => handleStatusUpdate(true)}>Active</Dropdown.Item>
                        <Dropdown.Item onClick={() => handleStatusUpdate(false)}>Inactive</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}

                  {/* <Button
                    variant="primary"
                    className="px-3 py-1 fs-13 rounded"
                    style={{
                      backgroundColor: "#3469E7",
                      borderColor: "#3469E7",
                    }}
                    onClick={() =>
                      navigate("/vendor/create", { state: { id: vendor.id } })
                    }
                  >
                    Edit
                  </Button> */}

                  <div className="d-flex flex-row flex-grow-1 align-items-end justify-content-end gap-2 ps-3">
                    <div onClick={(e) => e.stopPropagation()}>
                      {(checkPermission("edit_permission") || checkPermission("delete_permission")) && (
                        <Dropdown>
                          <Dropdown.Toggle
                            bsPrefix="custom-toggle"
                            variant="light"
                            id="dropdown-sort"
                            className="d-flex align-items-center justify-content-between rounded-5 bg-white"
                            style={{
                              width: "36px",
                              height: "36px",
                              border: "1px solid #EBEBEB",
                            }}
                          >
                            <span className="d-flex align-items-center px-1 gap-2">
                              <img src={moreIcon} alt="more" />
                            </span>
                          </Dropdown.Toggle>

                          <Dropdown.Menu
                            style={{
                              backgroundColor: "white",
                              borderRadius: "8px",
                              border: "1px solid #EBEBEB",
                              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                            }}
                          >
                            {checkPermission("edit_permission") && (
                              <Dropdown.Item
                                className="fs-14 fw-500 textcolor"
                                onClick={() => {
                                  navigate("/vendor/create", {
                                    state: { id: vendor.id },
                                  });
                                }}
                              >
                                Edit
                              </Dropdown.Item>
                            )}
                            {checkPermission("delete_permission") && (
                              <Dropdown.Item
                                className="fs-14 fw-500 textcolor"
                                onClick={() => {
                                  setDeleteId(id);
                                }}
                              >
                                Delete
                              </Dropdown.Item>
                            )}
                          </Dropdown.Menu>
                        </Dropdown>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between w-100 mt-2">
              {/* Left Section (Phone + Email) */}
              <div className="d-flex align-items-center gap-3">
                <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                  <FiPhone className="text-primary" />
                  {vendor.mobilenumber?.trim() ? sanitizeInput(String(vendor.mobilenumber || "")) : "-"}
                </div>

                <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                  <FiMail className="text-primary" />
                  {vendor.email?.trim() ? sanitizeInput(String(vendor.email || "")) : "-"}
                </div>
              </div>

              {/* Right Section (Login required) */}
              {/* <div
                  className={`d-flex align-items-center gap-1 fs-12 fw-500 ${
                    vendor.login_require ? "text-success" : "text-muted"
                  }`}
                >
                  {vendor.login_require ? <FiCheckCircle /> : <FiXCircle />}{" "}
                  Login Required
                </div> */}
            </div>
          </div>

          <Row className="g-3 mb-3">
            {/* Vendor Details Card */}
            <Col md={6}>
              <Card className="border-0 shadow-sm h-100 text-start">
                <Card.Body className="p-4">
                  <h6 className="fw-bold mb-4">Vendor details</h6>

                  <div className="d-flex justify-content-between mb-3">
                    <span className="text-muted fs-14 d-flex align-items-center gap-2">
                      {/* <FiUser size={16} /> */}
                      Vendor ID
                    </span>
                    <span className="fw-bold fs-14">{sanitizeInput(String(vendor.vendor_id || ""))}</span>
                  </div>

                  <div className="d-flex justify-content-between mb-4">
                    <span className="text-muted fs-14 d-flex align-items-center gap-2">GST NO</span>
                    <span className="fw-bold fs-14">{sanitizeInput(String(vendor.gst_number || "")) || "-"}</span>
                  </div>

                  <div className="border rounded-4 p-3 bg-white" style={{ borderStyle: "dashed" }}>
                    <p className="text-muted fs-12 mb-3">Bank Account Information</p>
                    <div className="d-flex align-items-center gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center fw-bold fs-18"
                        style={{
                          width: "48px",
                          height: "48px",
                          backgroundColor: "#E8F0FE",
                          color: "#3469E7",
                        }}
                      >
                        {vendor.bank_account_name ? vendor.bank_account_name.charAt(0).toUpperCase() : "N"}
                      </div>
                      <div>
                        <div className="fs-11 text-muted">Account Holder Name</div>
                        <div className="fw-bold fs-14">{sanitizeInput(String(vendor.bank_account_name || "")) || "-"}</div>
                        <div className="fs-11 text-muted mt-2">Account Number</div>
                        <div className="d-flex align-items-center gap-2 fs-13 text-muted">
                          {sanitizeInput(String(vendor.bank_account_number || "")) || "-"}
                          <span className="text-muted">•</span>
                          <span className="badge bg-white text-primary border px-2 py-1 fw-normal">IFSC Code : {sanitizeInput(String(vendor.ifsc_code || "")) || "-"}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Address Cards */}
            <Col md={6}>
              <div className="d-flex flex-column gap-3 h-100 text-start">
                <Card className="border-0 shadow-sm flex-fill">
                  <Card.Body className="p-4">
                    <h6 className="fw-bold mb-3">Billing address</h6>
                    <div className="d-flex gap-3">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: "#FFE5E5",
                        }}
                      >
                        <FiMapPin color="#FF3B30" size={16} />
                      </div>
                      <div>
                        <p className="fs-14 text-muted m-0 lh-base">{sanitizeInput(String(vendor.billing_address || ""))}</p>
                        <p className="fs-13 text-muted m-0">
                          {sanitizeInput(String(vendor.billing_city || ""))}, {sanitizeInput(String(vendor.billing_state || ""))}, {sanitizeInput(String(vendor.billing_country || ""))} - {sanitizeInput(String(vendor.billing_zipcode || ""))}
                        </p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>

                <Card className="border-0 shadow-sm flex-fill">
                  <Card.Body className="p-4">
                    <h6 className="fw-bold mb-3">Description</h6>
                    <div className="d-flex gap-3">
                      <div>
                        <p className="fs-14 text-muted m-0 lh-base">{sanitizeInput(String(vendor.description || "")) || "No description provided."}</p>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </div>
            </Col>
          </Row>
        </div>
      </div>
      <CommonSnackbar show={snackbar.show} message={snackbar.message} isSuccess={snackbar.status} onClose={handleSnackbarClose} />
    </>
  );
};

export default VendorView;
